"use client";

import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import Image from "next/image";
import Link from "next/link";
import { supabase } from "@/lib/supabase";
import { ClaimBanner } from "@/components/ClaimBanner";
import { AppDownloadBanner } from "@/components/product/AppDownloadBanner";
import { CommunityCaptureCta } from "@/components/product/CommunityCaptureCta";
import { MenuBadgeRow } from "@/components/menus/MenuBadgeRow";
import { capturePostHogEvent } from "@/lib/posthog-client";
import { POSTHOG_EVENTS, POSTHOG_SOURCE } from "@/lib/posthog-events";
import {
  resolveCanonicalRestaurantId,
  fetchRestaurantPublicStatus,
  isPubliclyClaimed,
} from "@menuhunter/core/publicRestaurantStatus";
import { formatPriceEurIfPresent } from "@/lib/formatPriceEur";
import { useLanguage } from "@/lib/useLanguage";
import { fetchMenuDishTranslations } from "@/lib/menuDishTranslations";
import type { Language } from "@/lib/translations";
import { MENU_DETAIL_HERO_DISPLAY, toThumbUrl } from "@/lib/images";

const DATE_LOCALES: Record<string, string> = {
  es: "es-ES",
  en: "en-GB",
  ca: "ca-ES",
};

type DishRow = {
  name: string;
  description?: string;
  allergens?: string[];
  tags?: string[];
};

type DishSection = {
  key: string;
  title: string;
  rows: DishRow[];
};

const SECTION_ALIAS_MAP: Array<{ canonical: string; aliases: string[] }> = [
  { canonical: "primeros", aliases: ["primeros", "primero", "starters", "starter", "entrantes", "first"] },
  { canonical: "segundos", aliases: ["segundos", "segundo", "mains", "main", "principales", "second"] },
  { canonical: "postres", aliases: ["postres", "postre", "desserts", "dessert"] },
  { canonical: "bebidas", aliases: ["bebidas", "bebida", "drinks", "drink"] },
];

interface MenuData {
  id: string;
  restaurant_id: string;
  restaurant_name: string;
  restaurant_address?: string | null;
  restaurant_city?: string | null;
  price_text?: string | null;
  price_eur?: number | null;
  photo_url?: string | null;
  captured_at?: string;
  menu_date?: string | null;
  publish_channel?: string | null;
  sections: DishSection[];
  hasStructuredText: boolean;
  /** undefined = public status unknown (do not show unclaimed CTA). */
  is_claimed: boolean | undefined;
}

// Detect if user is on mobile device
const isMobile = () => {
  if (typeof window === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
};

const PRESET_SECTION_RULES: Array<{ key: string; title: string; pattern: RegExp }> = [
  { key: "primeros", title: "Primeros / Starters", pattern: /primer|entrante|starter|first/i },
  { key: "segundos", title: "Segundos / Mains", pattern: /segundo|principal|main|second/i },
  { key: "postres", title: "Postres / Desserts", pattern: /postre|dessert|dulce/i },
  { key: "bebidas", title: "Bebidas / Drinks", pattern: /bebida|drink|vino|agua|refresco/i },
];

const toDishRow = (item: any): DishRow | null => {
  if (!item) return null;
  if (typeof item === "string") {
    const trimmed = item.trim();
    return trimmed ? { name: trimmed } : null;
  }
  if (typeof item !== "object") return null;
  const name = typeof item.name === "string" ? item.name.trim() : "";
  if (!name) return null;
  const description = typeof item.description === "string" ? item.description.trim() : undefined;
  const allergens = Array.isArray(item.allergens)
    ? item.allergens.filter((a: unknown) => typeof a === "string" && a.trim().length > 0).map((a: string) => a.trim())
    : undefined;
  const tags = Array.isArray(item.tags)
    ? item.tags.filter((t: unknown) => typeof t === "string" && t.trim().length > 0).map((t: string) => t.trim())
    : undefined;
  return { name, description, allergens, tags };
};

const normalizeSectionTitle = (raw: string, index: number): { key: string; title: string } => {
  const name = raw.trim();
  if (!name) return { key: `section-${index}`, title: `Sección ${index + 1}` };
  const preset = PRESET_SECTION_RULES.find((rule) => rule.pattern.test(name));
  if (preset) return { key: `${preset.key}-${index}`, title: preset.title };
  return { key: `section-${index}`, title: name };
};

const normalizeSectionToken = (value: string): string => {
  const base = value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  for (const def of SECTION_ALIAS_MAP) {
    if (def.aliases.some((alias) => base === alias || base.includes(alias))) {
      return def.canonical;
    }
  }
  return base;
};

const isSectionTitleDuplicateRow = (sectionTitle: string, rowName: string): boolean => {
  const sectionToken = normalizeSectionToken(sectionTitle);
  const rowToken = normalizeSectionToken(rowName);
  if (!sectionToken || !rowToken) return false;
  return sectionToken === rowToken;
};

const parseSectionsFromSource = (source: any): DishSection[] => {
  if (!source || typeof source !== "object") return [];
  const sections = Array.isArray(source.sections) ? source.sections : [];
  const mapped = sections
    .map((section: any, index: number): DishSection | null => {
      const rawName = typeof section?.name === "string" ? section.name : "";
      const { key, title } = normalizeSectionTitle(rawName, index);
      let rows = Array.isArray(section?.items)
        ? section.items.map(toDishRow).filter((row: DishRow | null): row is DishRow => row != null)
        : [];
      rows = rows.filter(
        (row: DishRow, rowIndex: number) => !(rowIndex === 0 && isSectionTitleDuplicateRow(title, row.name))
      );
      if (rows.length === 0) return null;
      return { key, title, rows };
    })
    .filter((section: DishSection | null): section is DishSection => section != null);
  return mapped;
};

const parseLegacySections = (data: any): DishSection[] => {
  const buckets = [
    { key: "primeros", title: "Primeros / Starters", rows: Array.isArray(data?.primeros) ? data.primeros : [] },
    { key: "segundos", title: "Segundos / Mains", rows: Array.isArray(data?.segundos) ? data.segundos : [] },
    { key: "postres", title: "Postres / Desserts", rows: Array.isArray(data?.postres) ? data.postres : [] },
    { key: "bebidas", title: "Bebidas / Drinks", rows: Array.isArray(data?.bebidas) ? data.bebidas : [] },
  ];
  return buckets
    .map((bucket) => ({
      key: bucket.key,
      title: bucket.title,
      rows: bucket.rows
        .map((dish: unknown) => (typeof dish === "string" ? dish.trim() : ""))
        .filter(Boolean)
        .map((name: string) => ({ name })),
    }))
    .filter((bucket) => bucket.rows.length > 0);
};

const parseRawTextToSections = (text: string | null | undefined): DishSection[] => {
  if (!text || typeof text !== "string") return [];
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .filter((line) => line.length > 2);
  if (lines.length === 0) return [];
  const chunk = Math.max(1, Math.ceil(lines.length / 3));
  return [
    { key: "primeros-fallback", title: "Primeros / Starters", rows: lines.slice(0, chunk).map((name) => ({ name })) },
    { key: "segundos-fallback", title: "Segundos / Mains", rows: lines.slice(chunk, chunk * 2).map((name) => ({ name })) },
    { key: "postres-fallback", title: "Postres / Desserts", rows: lines.slice(chunk * 2).map((name) => ({ name })) },
  ].filter((section) => section.rows.length > 0);
};

const buildMenuSections = (row: any): { sections: DishSection[]; menuDate: string | null } => {
  const canonical = row?.verification_status === "verified" ? row?.ocr_corrected_json : null;
  const canonicalSections = parseSectionsFromSource(canonical);
  if (canonicalSections.length > 0) {
    return {
      sections: canonicalSections,
      menuDate: typeof canonical?.menu_date === "string" ? canonical.menu_date : null,
    };
  }

  const legacyColumns = parseLegacySections(row);
  if (legacyColumns.length > 0) {
    return { sections: legacyColumns, menuDate: null };
  }

  const ocrV3Sections = parseSectionsFromSource(row?.ocr_parsed_v3);
  if (ocrV3Sections.length > 0) {
    return {
      sections: ocrV3Sections,
      menuDate: typeof row?.ocr_parsed_v3?.menu_date === "string" ? row.ocr_parsed_v3.menu_date : null,
    };
  }

  const ocrV3Legacy = parseLegacySections(row?.ocr_parsed_v3);
  if (ocrV3Legacy.length > 0) {
    return { sections: ocrV3Legacy, menuDate: null };
  }

  const extracted =
    typeof row?.dishes_extracted === "string"
      ? (() => {
          try {
            return JSON.parse(row.dishes_extracted);
          } catch {
            return null;
          }
        })()
      : row?.dishes_extracted;

  const extractedSections = parseSectionsFromSource(extracted);
  if (extractedSections.length > 0) {
    return {
      sections: extractedSections,
      menuDate: typeof extracted?.menu_date === "string" ? extracted.menu_date : null,
    };
  }

  const extractedLegacy = parseLegacySections(extracted);
  if (extractedLegacy.length > 0) {
    return { sections: extractedLegacy, menuDate: null };
  }

  const rawTextFallback = parseRawTextToSections(row?.menu_text_edited ?? row?.menu_text_raw ?? null);
  if (rawTextFallback.length > 0) {
    return { sections: rawTextFallback, menuDate: null };
  }

  return { sections: [], menuDate: null };
};

const formatCitySlug = (slug: string | null | undefined): string | null => {
  if (!slug || typeof slug !== "string") return null;
  return slug
    .split("-")
    .map((word) => (word.length > 0 ? word[0].toUpperCase() + word.slice(1) : word))
    .join(" ");
};

const COURSE_SECTION_TITLES: Record<Language, Record<"primeros" | "segundos" | "postres", string>> = {
  es: { primeros: "Primeros", segundos: "Segundos", postres: "Postres" },
  en: { primeros: "Starters", segundos: "Mains", postres: "Desserts" },
  ca: { primeros: "Primers", segundos: "Segons", postres: "Postres" },
};

function applyDishTranslations(
  sections: DishSection[],
  courses: { primeros: string[]; segundos: string[]; postres: string[] },
  language: Language
): DishSection[] {
  const byKey: Record<string, string[]> = {
    primeros: courses.primeros,
    segundos: courses.segundos,
    postres: courses.postres,
  };
  return sections.map((section) => {
    const baseKey = section.key.replace(/-fallback$/, "").replace(/-\d+$/, "");
    const translated = byKey[baseKey];
    if (!translated || translated.length === 0) return section;
    const title =
      baseKey === "primeros" || baseKey === "segundos" || baseKey === "postres"
        ? COURSE_SECTION_TITLES[language][baseKey]
        : section.title;
    return {
      ...section,
      title,
      rows: translated.map((name) => ({ name })),
    };
  });
}

export function MenuDetailClient() {
  const { language, t } = useLanguage();
  const m = t.menuDetailPage;
  const dateLocale = DATE_LOCALES[language] ?? "es-ES";
  const params = useParams();
  const menuId = params?.menuId as string;
  const [menu, setMenu] = useState<MenuData | null>(null);
  /** Source-language sections from the snapshot (never overwritten by MT). */
  const [sourceSections, setSourceSections] = useState<DishSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attemptingDeepLink, setAttemptingDeepLink] = useState(false);
  const openInAppHref = `menuhunter:///menu/${menuId}`;
  const downloadHref = "https://menuhunter.app/download";

  // Fetch menu data
  useEffect(() => {
    if (!menuId) {
      setError("Menu ID is required");
      setLoading(false);
      return;
    }

    const fetchMenu = async () => {
      try {
        const { data: rpcData, error: queryError } = await supabase.rpc('get_public_menu_detail', {
          p_menu_id: menuId,
        });

        const row = Array.isArray(rpcData) ? rpcData[0] : rpcData;
        if (queryError || !row) {
          setError("Menu not found");
          setLoading(false);
          return;
        }

        const data = row as Record<string, unknown>;
        const restaurant = (data.restaurant ?? null) as {
          name?: string;
          address_text?: string | null;
          city_slug?: string | null;
        } | null;
        const { sections, menuDate } = buildMenuSections(data);

        let isClaimed: boolean | undefined = undefined;
        if (data.restaurant_id) {
          const canonical = await resolveCanonicalRestaurantId(supabase, data.restaurant_id as string);
          const { map: publicStatusMap, error: publicStatusError } = await fetchRestaurantPublicStatus(
            supabase,
            [canonical]
          );
          if (!publicStatusError) {
            const pub = publicStatusMap.get(canonical);
            isClaimed = isPubliclyClaimed(pub);
          }
        }

        // Format price
        const priceText = formatPriceEurIfPresent(data.price_eur as number | null);

        setSourceSections(sections);
        setMenu({
          id: data.id as string,
          restaurant_id: data.restaurant_id as string,
          restaurant_name: restaurant?.name || "Unknown Restaurant",
          restaurant_address: restaurant?.address_text || null,
          restaurant_city: formatCitySlug(restaurant?.city_slug) || null,
          price_text: priceText,
          price_eur: data.price_eur as number | null,
          photo_url: (data.photo_url as string | null) ?? null,
          captured_at: data.captured_at as string,
          menu_date: menuDate,
          publish_channel: (data.publish_channel as string | null) ?? null,
          sections,
          hasStructuredText: sections.length > 0,
          is_claimed: isClaimed,
        });
      } catch (err) {
        console.error("Error fetching menu:", err);
        setError("Failed to load menu");
      } finally {
        setLoading(false);
      }
    };

    fetchMenu();
  }, [menuId]);

  // Overlay dish names in the viewer's language (shared links follow recipient locale).
  useEffect(() => {
    if (!menuId || sourceSections.length === 0) return;
    let cancelled = false;
    // Reset to source while fetching so language switches don't stack translations.
    setMenu((prev) => (prev && prev.id === menuId ? { ...prev, sections: sourceSections } : prev));
    void fetchMenuDishTranslations(menuId, language).then((courses) => {
      if (cancelled || !courses) return;
      setMenu((prev) => {
        if (!prev || prev.id !== menuId) return prev;
        return {
          ...prev,
          sections: applyDishTranslations(sourceSections, courses, language),
        };
      });
    });
    return () => {
      cancelled = true;
    };
  }, [menuId, language, sourceSections]);

  useEffect(() => {
    if (menuId) {
      capturePostHogEvent(POSTHOG_EVENTS.MENU_DETAIL_VIEWED, {
        source: POSTHOG_SOURCE,
        menu_id: menuId,
      });
    }
  }, [menuId]);

  // Attempt to open app when page loads (on mobile)
  useEffect(() => {
    if (!menu || !isMobile() || attemptingDeepLink) return;

    const attemptDeepLink = () => {
      setAttemptingDeepLink(true);
      const deepLinkUrl = `menuhunter:///menu/${menuId}`;

      setTimeout(() => {
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = deepLinkUrl;
        document.body.appendChild(iframe);

        setTimeout(() => {
          document.body.removeChild(iframe);
        }, 500);
      }, 500);
    };

    attemptDeepLink();
  }, [menu, menuId, attemptingDeepLink]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{m?.loading ?? "Cargando menú…"}</p>
        </div>
      </div>
    );
  }

  if (error || !menu) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            {m?.notFoundTitle ?? "Menú no encontrado"}
          </h1>
          <p className="text-gray-600 mb-6">
            {m?.notFoundBody ?? "El menú que buscas no existe o ha sido eliminado."}
          </p>
          <Link
            href="/"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
          >
            {m?.backHome ?? "Volver al inicio"}
          </Link>
        </div>
      </div>
    );
  }

  const locationLine = [menu.restaurant_address, menu.restaurant_city].filter(Boolean).join(" · ");
  const visibleDate = menu.menu_date || menu.captured_at || null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
      <div className="max-w-2xl mx-auto px-4 py-5 sm:py-8">
        <div className="mb-4">
          <AppDownloadBanner compact />
        </div>
        {menu && menu.is_claimed === false && menu.restaurant_id && (
          <ClaimBanner
            restaurantId={menu.restaurant_id}
            restaurantName={menu.restaurant_name}
            isClaimed={false}
          />
        )}

        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg p-5 sm:p-6 mb-4">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
                {menu.restaurant_name}
              </h1>
              <div className="flex flex-wrap items-center gap-2">
                {menu.price_text && (
                  <span className="inline-block bg-orange-500 text-white px-3 py-1.5 rounded-full font-semibold text-sm sm:text-base">
                    {menu.price_text}
                  </span>
                )}
                <MenuBadgeRow
                  capturedAt={menu.captured_at}
                  menuDate={menu.menu_date}
                  publishChannel={menu.publish_channel}
                  isRestaurantClaimed={menu.is_claimed}
                />
                {visibleDate && (
                  <p className="text-gray-600 dark:text-gray-300 text-xs sm:text-sm">
                    {new Date(visibleDate).toLocaleDateString(dateLocale, {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                )}
              </div>
              {locationLine && (
                <p className="text-gray-600 dark:text-gray-300 text-sm mt-3">
                  📍 {locationLine}
                </p>
              )}
              {!menu.hasStructuredText && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  {m?.menuTextUnavailableShort ?? "Texto del menú no disponible todavía."}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="sticky top-0 z-20 border border-blue-100 dark:border-gray-700 bg-white/95 dark:bg-gray-900/95 rounded-lg p-4 mb-4 backdrop-blur shadow-sm">
          <p className="text-sm text-gray-700 dark:text-gray-200 mb-3">
            {m?.openAppPrompt ??
              "Abre este menú en MenúHunter para ver más detalles y guardar favoritos."}
          </p>
          <div className="flex flex-wrap gap-2">
            <a
              href={openInAppHref}
              className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
            >
              {m?.openInApp ?? "Abrir en la app"}
            </a>
            <a
              href={downloadHref}
              className="inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-semibold text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              {m?.downloadApp ?? "Descargar la app"}
            </a>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-md p-5 sm:p-6 mb-4">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-5 text-center">
            {m?.menuOfTheDay ?? "Menú del día"}
          </h2>
          {menu.hasStructuredText ? (
            <div className="space-y-6">
              {menu.sections.map((section) => (
                <section key={section.key} className="text-center">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-px flex-1 bg-blue-100 dark:bg-gray-700" />
                    <h3 className="shrink-0 text-[11px] sm:text-xs font-semibold uppercase tracking-[0.12em] text-gray-700 dark:text-gray-200">
                      {section.title}
                    </h3>
                    <div className="h-px flex-1 bg-blue-100 dark:bg-gray-700" />
                  </div>
                  <ul className="mx-auto max-w-md space-y-2.5 sm:space-y-3">
                    {section.rows.map((row, index) => (
                      <li
                        key={`${section.key}-${index}`}
                        className="rounded-lg border border-gray-100 bg-gray-50/60 px-4 py-3 text-center dark:border-gray-800 dark:bg-gray-800/40 sm:px-5 sm:py-3.5"
                      >
                        <p className="text-sm font-medium leading-snug text-gray-900 dark:text-gray-100 sm:text-base">
                          {row.name}
                        </p>
                        {row.description ? (
                          <p className="mt-1.5 text-xs leading-relaxed text-gray-600 dark:text-gray-300">
                            {row.description}
                          </p>
                        ) : null}
                        {(row.tags && row.tags.length > 0) || (row.allergens && row.allergens.length > 0) ? (
                          <p className="mt-2 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
                            {row.tags && row.tags.length > 0 ? `${m?.tagsLabel ?? "Etiquetas"}: ${row.tags.join(", ")}` : ""}
                            {row.tags && row.tags.length > 0 && row.allergens && row.allergens.length > 0 ? " · " : ""}
                            {row.allergens && row.allergens.length > 0 ? `${m?.allergensLabel ?? "Alérgenos"}: ${row.allergens.join(", ")}` : ""}
                          </p>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                </section>
              ))}
            </div>
          ) : (
            <div className="rounded-lg border border-dashed border-gray-300 p-5 text-center dark:border-gray-700">
              <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-200">
                {m?.menuTextUnavailableLong ??
                  "Texto del menú no disponible todavía, pero puedes ver la imagen del menú subida."}
              </p>
            </div>
          )}
        </div>

        {menu.photo_url && (
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-md p-3 mb-6">
            <div className="rounded-lg overflow-hidden">
              <Image
                src={toThumbUrl(menu.photo_url, MENU_DETAIL_HERO_DISPLAY) ?? menu.photo_url}
                alt={(m?.photoAlt ?? "Menú de {name}").replace("{name}", menu.restaurant_name)}
                width={800}
                height={600}
                className="w-full h-auto"
                unoptimized
              />
            </div>
          </div>
        )}

        <div className="mb-6">
          <CommunityCaptureCta />
        </div>

        <div className="text-center mt-6">
          <Link
            href={`/restaurant/${menu.restaurant_id}`}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-semibold mr-4"
          >
            {m?.viewRestaurant ?? "Ver restaurante"}
          </Link>
          <Link
            href="/"
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 font-semibold"
          >
            ← {m?.backHome ?? "Volver al inicio"}
          </Link>
        </div>
      </div>
    </div>
  );
}
